<template>
  <div class="container">
    <h2 class="report-title">Mahadasha Analysis Report</h2>
    <h3 class="report-title">What This Report Covers</h3>
    <p style="text-align: justify;">
      The Mahadasha Analysis Report <b>reveals how planetary periods (Mahadashas) shape and unfold throughout your life</b>.
      Each Mahadasha is ruled by a different planet and influences
      various life areas over a specific time frame.
    </p>    

    <p>
      This report analyzes in depth about:
    </p>
    <ul>
      <li style="padding: 5px;"><b>Core Influence of the Mahadasha & House Activation</b>.</li>
      <li style="padding: 5px;">How does <b>life's direction change</b> according to the houses governed by the
        Mahadasha planet?</li>
      <li style="padding: 5px;">What <b>new priorities</b> surface based on the activated houses?</li>
      <li style="padding: 5px;">Key <b>life areas that transform</b> due to this activation.</li>
    </ul>

    <h3 class="report-title">How This Report Helps You</h3>
    <ul>
      <li style="padding: 5px;"><b>Gain valuable lessons, recognize patterns</b>, and understand how past Mahadasha
        periods have shaped your life so far.</li>
      <li style="padding: 5px;">Identify which <b>planetary period you are currently in</b> and how it impacts your life.</li>
      <li style="padding: 5px;">Prepare for <b>upcoming planetary periods</b> and align with their energies.</li>
      <li style="padding: 5px;">Gain <b>clarity on opportunities, challenges, and life themes</b> during each phase</li>  
    </ul>

    <h3 class="report-title">How This Report Is Unique</h3>
    <p style="text-align: justify;">
      Unlike traditional astrology readings that give generalized Mahadasha insights, this
      report offers <b>highly personalized Mahadasha insights according to the age & life stage</b> of
      the individual. Mahadasha insights also include <b>probable real-world scenarios</b> based on
      general life progression.
    </p>

    <h3 class="report-title">Why You Should Get This Report</h3>
    <p style="text-align: justify;">
      If you want a <b>roadmap for your life's unfolding journey and a clear understanding
      of upcoming phases</b>, this report is essential. It helps you navigate planetary influences
      with confidence and preparedness.     
    </p>

    <h3 class="bonus-title">Exclusive Bonus: Your Essential Natal Chart</h3>
    <p style="text-align: justify;">
      Each of our reports includes <b>Your Essential Natal Chart</b>, meticulously crafted using high-precision ephemeris data to ensure absolute accuracy in planetary positions. 
      Every calculation in your essential natal chart is performed with utmost precision, adhering to the core principles of Vedic astrology. 
      <b>Spanning 23 pages</b>, your Essential Natal Chart provides a comprehensive foundation for deeper astrological insights. 
      It includes <b>planetary positions, strengths, aspects, conjunctions, dignity, astrological house cusps and midpoints, 20 divisional charts, Vimsottari Dasha periods, and natal Panchanga</b>. 
      Designed as a valuable <b>companion to your personalized astrology reports</b>, it enhances your understanding of planetary influences and life themes with unparalleled accuracy.
    </p>

    <h3 class="bonus-title">Exclusive Bonus: Introduction to Vedic Astrology Guide</h3>
    <p style="text-align: justify;">
      Each of our reports includes an <b>Introduction to Vedic Astrology Guide</b>, carefully designed to help you build a strong foundation in this ancient system. 
      This guide covers essential concepts such as the <b>9 planets, 12 zodiac signs, 12 houses, 27 nakshatras, planetary dignity, dasha cycles, and transits</b>. 
      More importantly, with multiple example interpretations, it <b>explains how planets, signs, and houses interact</b> to shape planetary influences.
      By understanding these key principles, <b>you'll develop the confidence and clarity to interpret your reports</b> with greater accuracy. 
      This foundational knowledge empowers you to navigate your astrological exploration with depth, meaning, and a newfound sense of clarity.
    </p>

    <h3 class="bonus-title">Exclusive Bonus: Your Moon Sign and Nakshatra Report</h3>
    <p style="text-align: justify;">
      Each of our reports includes a basic yet insightful understanding of 
      your core personality by exploring three key elements: 
    </p>
    <ul>
      <li style="padding: 5px;"><strong>Ascendant (Lagna): </strong> Your outward personality, approach to life, and physical traits.</li>
      <li style="padding: 5px;"><strong>Moon Sign (Rashi) :</strong> Your emotional nature, mental tendencies, and subconscious mind.</li>
      <li style="padding: 5px;"><strong>Lunar Mansion (Nakshatra) :</strong> The deeper karmic and psychological blueprint influencing your thoughts, behavior, and destiny.</li>
    </ul>

    <br/>

    <div style="text-align: center;">
      <h3 style="color: #1b5e20;"><strong>Cost: ${{orderPrice}} USD</strong></h3>
    </div>
    
    <div align="center">
      <a @click="activateMenuItem('paid-report')" class="cta-button">Back</a>
      &nbsp;
      <a @click="showSampleReport()" class="cta-button">View Sample Report</a>
    </div>

    <div class="payment-methods-container">
      <div class="apple-pay" @click="stripePurchase('apple-pay')"><img src="../assets/apay.png" alt="Apple Pay" width="80" /></div>
      <div class="google-pay" @click="stripePurchase('google-pay')"><img src="../assets/gpay.png" alt="Apple Pay" width="80" /></div>
      <div class="credit-cards" @click="stripePurchase('credit-cards')"><img src="../assets/stripe-credit-cards.png" alt="Apple Pay" width="200" /></div>
    	<div class="paypal-container" ref="paypalContainerMAR"></div>
	  </div>
  </div>

  <div class="powered-by-wrapper">
    <p class="powered-text">💳 Payments securely processed via PayPal/Stripe for our parent site <a href="https://www.vedicastrochart.com" target="_blank">www.vedicastrochart.com</a>.</p>
  </div>

</template>

<script>
import HTTP from '../http-axios';
import ApiBaseUrl from '../http-api-base-url'
import { queryString, generatePaidNatalChartKey } from '@/util.js'

export default {
  props: {
    horoscope: {
      type: Object
    }
  },
  data() {
    return {
      orderDescription: 'Mahadasha Analysis Report from www.vedicastrologyreport.com',
      orderPrice: '14.95',
      orderCompleted: false,
      paypalOrder: {
        orderId: '',
        payerGivenName: '',
        payerSurname: '',
        payerEmail: '',
        orderData: '',
        orderPrice: '',
      }
    }
  },
  mounted() {

    window.scrollTo(0, 0); // Instantly scroll to top when component is mounted

    let serverUrl = ApiBaseUrl();

    const script = document.createElement("script");

    if ( serverUrl.includes('api.vedicastrochart.com') ) {

      // PRODUCTION - LIVE
      script.src = "https://www.paypal.com/sdk/js?client-id=AdmcXR3kme4bPK1lX05Gx5J03DCwudEhCnKPDU23FRNDbiz8ReoYPDIaGH68T828IthtIqzwFDjxcPkr&currency=USD&disable-funding=credit,card";

    } else {

      // SANDBOX - TESTING
      script.src = "https://www.paypal.com/sdk/js?client-id=Af_ir11mM_o7A22aqOu2azj9FSXrfO2h_xjOeLP0hLgv0LR93ddjRAqxtQoVcYUJ_PMgP2lubzB3isGI&currency=USD&disable-funding=credit,card";

    }

    script.addEventListener("load", this.setLoaded)
    document.body.appendChild(script)
  },

  methods: {

		activateMenuItem( activeMenuItem ) {
			this.$emit('activeMenuItem', activeMenuItem);
		},

    setLoaded() {
      paypal.Buttons({
        // Sets up the transaction when a payment button is clicked
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [{
              description: this.orderDescription,
              amount: {
                value: this.orderPrice,
                currency_code: "USD"
              }
            }],
            application_context: {
              shipping_preference: "NO_SHIPPING",
            }
          });
        },
        // Finalize the transaction after payer approval
        onApprove: (data, actions) => {

          const orderHandler = (orderData) => {
            var orderJsonString = JSON.stringify(orderData, null, 2);
            console.log('Capture result', orderData, orderJsonString);
            this.purchaseSuccess(orderData.id, orderData.payer.name.given_name, orderData.payer.name.surname, orderData.payer.email_address, orderJsonString);
          };

          return actions.order.capture().then(orderHandler);
        },

        onError: (err) => {
          console.log(JSON.stringify(err, null, 2));
          this.purchaseFailure(JSON.stringify(err, null, 2))
        }

      }).render(this.$refs.paypalContainerMAR);

    },

    purchaseSuccess: function (orderId, payerGivenName, payerSurname, payerEmail, orderJsonString) {

      // Set paypal order details
      this.paypalOrder.orderId = orderId;
      this.paypalOrder.payerGivenName = payerGivenName;
      this.paypalOrder.payerSurname = payerSurname;
      this.paypalOrder.payerEmail = payerEmail;
      this.paypalOrder.orderData = orderJsonString;
      this.paypalOrder.orderPrice = this.orderPrice;

      // Track paypal order success
      HTTP().post(`/site/var/api/purchase/report/mahadashaanalysis/paypal/order/success?` + this.queryString(this.horoscope.criteria) + '&' + this.queryString(this.paypalOrder))
        .then(response => {
          console.log('order success:');
          document.documentElement.innerHTML = response.data
        })
        .catch(e => {
          console.log(e)
        })
    },

    purchaseFailure: function (errorMessage) {


      // Track paypal order failure
      HTTP().post(`/site/var/api/purchase/report/mahadashaanalysis/paypal/order/failure?` + this.queryString(this.horoscope.criteria) + '&error=' + errorMessage)
        .then(response => {
          console.log('order failure:' + errorMessage);
          alert("Error while completing your PayPal Order : " + errorMessage);
        })
        .catch(e => {
          console.log(e)
        })
    },

    queryString: function (obj) {
        return queryString(obj);
    },
    getCriteriaToken: function() {
        return generatePaidNatalChartKey(this.horoscope.criteria);
    },

    stripePurchase: function(paymentMethod) {
      HTTP().post(`/site/var/api/purchase/report/mahadashaanalysis/stripe/order/checkout?` + this.queryString(this.horoscope.criteria) + '&pm=' + paymentMethod)
        .then(response => {
		      window.location.href = response.data	
        })
        .catch(e => {
          console.log(e)
        })
	  },

    showSampleReport() {
        window.open(ApiBaseUrl() + '/site/var/api/report/sample/pmar?' + this.queryString(this.horoscope.criteria), "_blank");
    },  
  }

}

</script>

<style scoped>
body {
  font-family: Arial, sans-serif;
  margin: 0;
  padding: 0;
  background-color: #ffffff;
  color: #333;
}
.container {
  max-width: 800px;
  margin: 30px auto;
  padding-right: 25px;
  padding-left: 25px;
  padding-top: 5px;
  border-radius: 12px;
  background: #EDF6F9; /* Updated background color */
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
  border: 0px solid;
  line-height: 1.5;
}
.report-title {
  color: #087F8C;
  text-align: left;  
}
.bonus-title {
  color: orangered;
  text-align: left;  
}
p, ul {
  text-align: left;
}
ul {
  padding-left: 20px;
}
.cta-button {
  display: inline-block;
  padding: 12px 25px;
  background-color: #087F8C;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  margin: 20px 0;
  font-weight: bold;
  text-align: center;
  width: fit-content;
  cursor: pointer;
}
.cta-button:hover {
  background-color: #218838;
}

.payment-methods-container {
  margin: auto;
	border: 0px solid;
	width: 90%;
	padding: 10px;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-evenly;
	padding: 10px;
  cursor: pointer;
}

@media (max-width: 768px) {
  .container {
    max-width: 100%;
    padding: 15px;
    margin: 15px;
    border-radius: 6px;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);    
  }
  .cta-button {
    width: fit-content;
    padding: 12px 20px;
    margin: 20px 0;
  }
  .payment-methods-container {
    margin: 5px auto;
    border: 0px solid;
    width: 90%;
    padding: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;	
    cursor: pointer;
  }
  .apple-pay {
    margin: 10px;
  }
  .google-pay {
    margin: 10px;
  }
  .credit-cards {
    margin: 10px;
  }
  .paypal-container {
    margin: 10px;
  }
}

.powered-by-wrapper {
  margin-top: 24px;
  padding: 16px;
  background-color: #f4f8fb;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.powered-text {
  margin: 4px 0;
}

.powered-note {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}

.powered-text a, .powered-note a {
  color: #2b72d6;
  text-decoration: none;
}

.powered-text a:hover, .powered-note a:hover {
  text-decoration: underline;
}

@media (max-width: 600px) {
  .powered-by-wrapper {
    font-size: 13px;
  }
}
</style>
